import * as React from 'react';
import type { LucideIcon } from './icons';

export type IconSize = 'sm' | 'md' | 'lg' | number;

export interface IconProps extends Omit<React.SVGAttributes<SVGSVGElement>, 'ref'> {
  /** The Lucide icon component to render. */
  as: LucideIcon;
  /**
   * Size: 'sm' = 14px, 'md' = 18px, 'lg' = 20px, or an explicit pixel number.
   * Defaults to 'md'.
   */
  size?: IconSize;
  /** Stroke width. Default 1.75 — slightly lighter than Lucide's default 2. */
  strokeWidth?: number;
  /**
   * Screen-reader label. If omitted and aria-hidden is not set, the icon is
   * implicitly decorative and will be hidden from assistive tech.
   */
  'aria-label'?: string;
  /** React 19 ref-as-prop. */
  ref?: React.Ref<SVGSVGElement>;
}

const SIZE_MAP: Record<Exclude<IconSize, number>, number> = {
  sm: 14,
  md: 18,
  lg: 20,
};

/**
 * Icon — wrapper around lucide-react glyphs that normalises size
 * (`sm`/`md`/`lg` → 14/18/20px or pass a numeric override), stroke
 * width (default 1.75 — slightly lighter than lucide's default 2),
 * and accessibility (`aria-hidden=true` by default; pass `aria-label`
 * to surface as a named graphic to assistive tech).
 */
export function Icon({ as: Glyph, size = 'md', strokeWidth = 1.75, ref, ...rest }: IconProps) {
  const px = typeof size === 'number' ? size : SIZE_MAP[size];
  const hasLabel = Boolean(rest['aria-label']);
  return (
    <Glyph
      ref={ref}
      size={px}
      strokeWidth={strokeWidth}
      aria-hidden={hasLabel ? undefined : true}
      focusable="false"
      {...rest}
    />
  );
}
Icon.displayName = 'Icon';
